import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePage } from '../../hooks/usePage';
import {
  shopApi,
  VendorDetail as VendorDetailData,
  VendorStatus,
  AdminProduct,
  AdminOrder,
  VendorPayout,
  OrderStatus,
  PayoutStatus,
} from '../../api/shop';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { PageHeader } from '../../components/ui/PageHeader';
import { Table } from '../../components/ui/Table';
import { VendorAvatar } from '../../components/ui/VendorAvatar';
import { useToast } from '../../hooks/useToast';

const STATUS_VARIANTS: Record<
  VendorStatus,
  'success' | 'warning' | 'danger' | 'neutral'
> = {
  active: 'success',
  pending: 'warning',
  suspended: 'danger',
};

const ORDER_STATUS_VARIANTS: Record<
  OrderStatus,
  'success' | 'warning' | 'danger' | 'neutral'
> = {
  placed: 'neutral',
  processing: 'warning',
  shipped: 'warning',
  out_for_delivery: 'warning',
  delivered: 'success',
  completed: 'success',
  cancelled: 'danger',
  disputed: 'danger',
};

const PAYOUT_STATUS_VARIANTS: Record<
  PayoutStatus,
  'success' | 'warning' | 'danger' | 'neutral'
> = {
  pending: 'warning',
  processing: 'warning',
  completed: 'success',
  failed: 'danger',
};

const PRODUCT_STATUS_VARIANTS: Record<
  AdminProduct['status'],
  'success' | 'warning' | 'danger' | 'neutral'
> = {
  active: 'success',
  draft: 'warning',
  archived: 'neutral',
};

const titleCase = (s: string) =>
  s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatNgn = (n: number) =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(n);

const formatAdminName = (vendor: VendorDetailData['vendor']) => {
  const admin = vendor.onboardedBy;
  return admin ? `${admin.firstName} ${admin.lastName}`.trim() : null;
};

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <div style={{ fontSize: 13, color: 'var(--neutral-500)' }}>{label}</div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: 'var(--neutral-800)',
          marginTop: 6,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      {hint ? (
        <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 4 }}>
          {hint}
        </div>
      ) : null}
    </Card>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontSize: 16,
        fontWeight: 600,
        color: 'var(--neutral-800)',
        margin: '24px 0 12px',
      }}
    >
      {children}
    </h2>
  );
}

export const VendorDetail: React.FC = () => {
  usePage('Vendor');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [detail, setDetail] = useState<VendorDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusBusy, setStatusBusy] = useState(false);
  const [featuredBusy, setFeaturedBusy] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await shopApi.getVendorDetail(id);
      setDetail(data);
    } catch (err) {
      toast.error(err, 'Failed to load vendor.');
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  async function handleStatus(next: VendorStatus) {
    if (!id) return;
    try {
      setStatusBusy(true);
      if (next === 'active') {
        await shopApi.activateVendor(id);
      } else {
        await shopApi.suspendVendor(id);
      }
      toast.success(`Vendor marked ${titleCase(next)}.`);
      await fetchDetail();
    } catch (err) {
      toast.error(err, 'Failed to update vendor status.');
    } finally {
      setStatusBusy(false);
    }
  }

  async function handleFeatured(product: AdminProduct) {
    try {
      setFeaturedBusy(product.id);
      await shopApi.setProductFeatured(product.id, !product.featured);
      toast.success(
        product.featured
          ? `"${product.name}" removed from featured.`
          : `"${product.name}" is now featured.`,
      );
      await fetchDetail();
    } catch (err) {
      toast.error(err, 'Failed to update featured flag.');
    } finally {
      setFeaturedBusy(null);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 48, display: 'flex', justifyContent: 'center' }}>
        <Spinner />
      </div>
    );
  }

  if (!detail) {
    return (
      <>
        <PageHeader
          title="Vendor not found"
          actions={
            <Button variant="secondary" onClick={() => navigate('/vendors')}>
              ← Back to vendors
            </Button>
          }
        />
      </>
    );
  }

  const { vendor, earnings, stats, products, recentOrders, payouts } = detail;
  const onboardedBy = formatAdminName(vendor);

  return (
    <>
      <PageHeader
        title={vendor.name}
        description={`${vendor.slug}${vendor.location ? ` · ${vendor.location}` : ''}`}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Badge variant={STATUS_VARIANTS[vendor.status]}>
              {titleCase(vendor.status)}
            </Badge>
            {vendor.status === 'active' ? (
              <Button
                variant="secondary"
                disabled={statusBusy}
                onClick={() => handleStatus('suspended')}
              >
                Suspend
              </Button>
            ) : (
              <Button
                variant="secondary"
                disabled={statusBusy}
                onClick={() => handleStatus('active')}
              >
                {vendor.status === 'pending' ? 'Approve' : 'Reactivate'}
              </Button>
            )}
            <Button variant="secondary" onClick={() => navigate('/vendors')}>
              ← Back
            </Button>
          </div>
        }
      />

      {/* Identity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
        <VendorAvatar name={vendor.name} avatarUrl={vendor.avatarUrl} size={56} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--neutral-800)' }}>
            {vendor.name}
          </div>
          {vendor.tagline ? (
            <div style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
              {vendor.tagline}
            </div>
          ) : null}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 18px',
          margin: '10px 0 20px 70px',
          fontSize: 13,
          color: 'var(--neutral-500)',
        }}
      >
        <span>
          Onboarded by{' '}
          <strong style={{ color: 'var(--neutral-700)' }}>
            {onboardedBy ?? 'Not recorded'}
          </strong>
        </span>
        <span>on {formatDate(vendor.createdAt)}</span>
      </div>

      {/* Earnings + stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
          gap: 12,
        }}
      >
        <StatTile
          label="Payout balance"
          value={formatNgn(earnings.payoutBalance)}
          hint={earnings.hasPayoutBank ? 'Bank on file' : 'No payout bank set'}
        />
        <StatTile
          label="Locked in escrow"
          value={formatNgn(earnings.lockedInEscrow)}
        />
        <StatTile
          label="Pending payouts"
          value={formatNgn(earnings.pendingPayouts)}
        />
        <StatTile
          label="Lifetime released"
          value={formatNgn(earnings.lifetimeReleased)}
          hint={`${formatNgn(earnings.releasedLast30Days)} last 30d`}
        />
        <StatTile label="Total orders" value={String(stats.totalOrders)} />
        <StatTile
          label="Products"
          value={String(stats.totalProducts)}
          hint={`${stats.activeProducts} active · ${stats.featuredProducts} featured`}
        />
      </div>

      {/* Products */}
      <SectionTitle>Products</SectionTitle>
      <Card>
        <Table<AdminProduct>
          data={products}
          emptyMessage="This vendor has no products yet."
          columns={[
            {
              key: 'name',
              header: 'Product',
              render: (p) => (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {p.imageUrl ? (
                    <img
                      src={p.imageUrl}
                      alt=""
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 'var(--radius-sm)',
                        objectFit: 'cover',
                      }}
                    />
                  ) : null}
                  <div>
                    <div style={{ fontWeight: 600 }}>{p.name}</div>
                    {p.brand ? (
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                        {p.brand}
                      </div>
                    ) : null}
                  </div>
                </div>
              ),
            },
            {
              key: 'price',
              header: 'Price',
              render: (p) => (
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatNgn(p.price)}
                </span>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              render: (p) => (
                <Badge variant={PRODUCT_STATUS_VARIANTS[p.status]}>
                  {titleCase(p.status)}
                </Badge>
              ),
            },
            {
              key: 'featured',
              header: 'Featured',
              render: (p) =>
                p.featured ? (
                  <Badge variant="success">Featured</Badge>
                ) : (
                  <span style={{ color: 'var(--neutral-400)' }}>—</span>
                ),
            },
            {
              key: 'actions',
              header: '',
              render: (p) => (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="secondary"
                    disabled={featuredBusy === p.id}
                    onClick={() => handleFeatured(p)}
                  >
                    {p.featured ? 'Unfeature' : 'Feature'}
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Recent orders */}
      <SectionTitle>Recent orders</SectionTitle>
      <Card>
        <Table<AdminOrder>
          data={recentOrders}
          emptyMessage="No orders yet."
          columns={[
            {
              key: 'reference',
              header: 'Reference',
              render: (o) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{o.reference}</div>
                  <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                    {o.items.length} item{o.items.length === 1 ? '' : 's'}
                  </div>
                </div>
              ),
            },
            {
              key: 'total',
              header: 'Total',
              render: (o) => (
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatNgn(o.total)}
                </span>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              render: (o) => (
                <Badge variant={ORDER_STATUS_VARIANTS[o.status]}>
                  {titleCase(o.status)}
                </Badge>
              ),
            },
            {
              key: 'placedAt',
              header: 'Placed',
              render: (o) => formatDate(o.placedAt),
            },
          ]}
        />
      </Card>

      {/* Payouts */}
      <SectionTitle>Payout requests</SectionTitle>
      <Card>
        <Table<VendorPayout>
          data={payouts}
          emptyMessage="No payout requests yet."
          columns={[
            {
              key: 'reference',
              header: 'Reference',
              render: (p) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{p.reference}</div>
                  {p.accountNumber ? (
                    <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                      {p.accountName} · {p.accountNumber}
                    </div>
                  ) : null}
                </div>
              ),
            },
            {
              key: 'amount',
              header: 'Amount',
              render: (p) => (
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatNgn(p.amount)}
                </span>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              render: (p) => (
                <Badge variant={PAYOUT_STATUS_VARIANTS[p.status]}>
                  {titleCase(p.status)}
                </Badge>
              ),
            },
            {
              key: 'createdAt',
              header: 'Requested',
              render: (p) => formatDate(p.createdAt),
            },
          ]}
        />
      </Card>
    </>
  );
};
